import { defineStore } from 'pinia';
import { authenticationHandler, parseJwt } from '@shared/authenticationHandler';
import type { LoginModel } from '@core/models/loginModel';
import type { ServiceResult } from '@core/models/actionResponse';
import { isServiceSuccess } from '@core/models/actionResponse';
import type { TokenResponse } from '@shared/models/authModels';
import {UserPreferencesDisplayModel, UserPreferencesUpdateModel} from "@shared/models/user/userPreferences";
import {fetchUserPreferences, updateUserPreferences} from "@shared/services/userPreferences.service";
const NAME = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name';
const EMAIL = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress';
const USER_ID = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';
const PERMISSIONS = 'millersstudioecomm/permission';
const STUDIO_ID = 'studio_id';

interface Claims {
  [NAME]: string;
  [EMAIL]: string;
  [USER_ID]: string;
  [PERMISSIONS]: string | string[];
  [STUDIO_ID]: number | null;
}

export const useAccountStore = defineStore('account', {
  state: () => ({
    token: null as string | null,
    claims: {} as Claims,
    userPreferences: {} as UserPreferencesDisplayModel,
  }),
  getters: {
    loggedIn: (state) => !!state.token,
    name: (state) => state.claims[NAME] || 'Anonymous',
    email: (state) => state.claims[EMAIL],
    userId: (state) => state.claims[USER_ID],
    permissions: (state) => {
      const v = state.claims[PERMISSIONS];
      if (!v) return [];
      return typeof v === 'string' ? [v] : v;
    },
    studioId: (state) => {
      return state.claims[STUDIO_ID];
    },
  },
  actions: {
    async initialize() {
      if (this.token) {
        return;
      }
      const tokenResponse = await authenticationHandler.getToken();
      if (isServiceSuccess(tokenResponse)) {
        this.processToken(tokenResponse.data);
      }
      return tokenResponse;
    },
    processToken(token: TokenResponse) {
      this.token = token.token;
      this.claims = parseJwt(token.token);
    },
    hasPermission(permission: string) {
      return this.permissions.includes(permission);
    },
    async login(model: LoginModel): Promise<ServiceResult<TokenResponse>> {
      const { username, password } = model;
      const response = await authenticationHandler.getToken({ username, password });
      if (isServiceSuccess(response)) {
        this.processToken(response.data);
      }
      return response;
    },
    async loginWithToken(token: string) {
      const response = await authenticationHandler.loginWithRefreshToken(token);
      if (isServiceSuccess(response)) {
        this.processToken(response.data);
      }
      return response;
    },
    async loadUserPreferences() {
      const userPreferences = await fetchUserPreferences();

      if (isServiceSuccess(userPreferences)) {
        this.userPreferences = {
          id: userPreferences.data.id,
          userId: userPreferences.data.userId,
          buildMaximumCropperSize: userPreferences.data.buildMaximumCropperSize ?? 200, // Default value
          buildImagesPerRow: userPreferences.data.buildImagesPerRow ?? 6,              // Default value
          useDarkMode: userPreferences.data.useDarkMode ?? false,                      // Default value
        };
      }
    },
    updateName(name: string) {
      this.claims[NAME] = name;
    },
    logout() {
      this.token = null;
      authenticationHandler.clear();
    },
    async saveUserPreferences() {

      const data = {
        buildMaximumCropperSize: this.userPreferences.buildMaximumCropperSize,
        buildImagesPerRow: this.userPreferences.buildImagesPerRow,
        useDarkMode: this.userPreferences.useDarkMode,
      } as UserPreferencesUpdateModel;

      const result = await updateUserPreferences(data);
      return result;
    }
  },
});
